import { useMemo } from 'react';
import { CookieService } from '@cx-web/feature-cookies';
import TrackingActions, { TRACKING_ACTIONS, TRACKING_CATEGORIES, TRACKING_EVENTS } from '@cx-web/feature-tracking';
import axios, { type AxiosResponse } from 'axios';

import { type Country } from '@cx-modules/constants';
import { type StateCode } from '@cx-modules/types';

import { useRepubContext } from '~/providers/repub-context';
import { getNameFromPointSpreadValue, getPointSpreadValueFromName, isStrictlyUSHandicap } from '~/utils/us-utils';

import LocalStorageService from '../localstorage-actions';
import { type Bet, type BetslipUtils, type TrackBet } from '../types';
import {
  calculateBookieOdds,
  calculateBookieOddsAndSelectedBookieOdds,
  calculateSelectedBookieOdds,
  getBestOdds,
  getBestOddsFromBet,
  getOdds,
  getSelectedBookieBetOdds,
} from '../utils/betslip-utils';

export const useBetslipUtils = (): BetslipUtils => {
  const { repub } = useRepubContext();

  const cookieService = useMemo(() => new CookieService(), []);
  const localStorageService = useMemo(() => new LocalStorageService(), []);
  const trackingActions = useMemo(() => new TrackingActions(), []);

  const getBetMeta = (
    bet: Bet,
  ): {
    betId: number;
    betName: string;
    marketName: string;
    spread: string | number;
    subeventName: string;
  } => {
    const strictlyUS = isStrictlyUSHandicap(bet);

    const { betId, marketName, name, subeventName } = bet;
    const betName = (strictlyUS && getNameFromPointSpreadValue(name)) || name;
    const spread = (strictlyUS && getPointSpreadValueFromName(name)) || '';

    return {
      betId,
      betName,
      subeventName,
      marketName,
      spread,
    };
  };

  // TODO: work to merge getBetslipAPI and getBetslipData
  // with migration of api from wildcard route api.cx.$
  // to api.cx.betslip will be completed under
  // separate ticket CIC-1232
  const getBetslipAPI = (
    betIds: number[],
    countryCode: Country,
    state: StateCode | '',
    completion: (success: any, res: any) => void,
  ): void => {
    axios({
      method: 'GET',
      url: `/api/cx/sports-catalogue/v1/betslip?betIds=${betIds}`,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Cache-Control': 'no-store, no-cache, must-revalidate, max-age=0',
        Expires: 'Thu, 01 Jan 1970 00:00:00 GMT',
        Pragma: 'no-cache',
        'subdivision-code': state,
        'country-code': countryCode,
        repub,
      },
    })
      .then((res: AxiosResponse) => {
        completion({ success: true }, res.data);
      })
      .catch((e: any) => {
        console.log(e);
        completion({ success: false }, e);
      });
  };

  const getBetslipCookieArray = (): [string, string][] => {
    const betslipCookieArray = cookieService.select('intBetslip').getArray(',', (item: string) => {
      return item.split('~');
    });

    // Ensure we filter out non-tuple data
    return betslipCookieArray.filter(c => !!c?.[1]);
  };

  const getLocalStorageBetslip = () => localStorageService.select('intBetslip').getJSON() || {};

  const getBetSelectedBookieFromLocalStore = (betId: string): string | null => {
    const localStorageBetslip = getLocalStorageBetslip();

    if (typeof localStorageBetslip[betId] !== 'undefined') {
      return localStorageBetslip[betId].selectedBookie;
    }

    return null;
  };

  const trackBetSlip = (betSlip: TrackBet[], stake?: number): void => {
    const isAcca = betSlip.length > 1;

    trackingActions.eventTracking({
      event: TRACKING_EVENTS.BET_SLIP_VIEW,
      eventCategory: TRACKING_CATEGORIES.BETTING,
      eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_VIEW],
      eventLabel: isAcca ? 'multi_bets' : 'single_bet',
      accumulator: isAcca ? 'yes' : 'no',
      marketName: betSlip.map(b => b.marketName).join(','),
      subeventName: betSlip.map(b => b.subeventName).join(','),
      bookieCode: betSlip.map(b => b.bestOddsBookmakers).join(','),
      betName: betSlip.map(b => b.name).join(','),
      betOdds: betSlip.map(b => (b.bestOddsUs ? b.bestOddsUs : b.bestOddsDecimal)).join(','),
      betStake: stake?.toString(),
    });
  };

  const postOddsClick = (betId: number, bookmakerCode: string, countryCode: Country, state: StateCode | ''): void => {
    axios({
      method: 'POST',
      url: '/api/cx/sports-catalogue/v1/odds-click',
      headers: {
        'subdivision-code': state,
        'country-code': countryCode,
        repub,
      },
      data: { betId, bookmakerCode },
    }).catch(() => {});
  };

  return {
    getBetMeta,
    getBetslipAPI,
    getBetslipCookieArray,
    getOdds,
    getSelectedBookieBetOdds,
    getBestOdds,
    getBestOddsFromBet,
    getLocalStorageBetslip,
    getBetSelectedBookieFromLocalStore,
    postOddsClick,
    trackBetSlip,
    calculateBookieOdds,
    calculateSelectedBookieOdds,
    calculateBookieOddsAndSelectedBookieOdds,
  };
};
